import type { ComponentType } from 'react';
import {
  Activity,
  BarChart3,
  Bell,
  BookOpen,
  Brain,
  FileText,
  Grid3x3,
  Hash,
  LayoutDashboard,
  MessageSquare,
  Network,
  Plug,
  Server,
  Swords,
  Target,
  TestTube2,
  Ticket,
  Users,
  Zap,
} from 'lucide-react';
import type { ConnectedIntegration } from './context/IntegrationsContext';
import { PROVIDER_NAV_TEMPLATES } from './context/IntegrationsContext';
import { INTEGRATIONS_BASE, NOTIFICATION_CENTER_BASE, SLACK_BASE } from './paths';

export interface IntegrationMenuItem {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  children?: IntegrationMenuItem[];
  glowColor?: string;
}

const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Users,
  Hash,
  Network,
  FileText,
  Activity,
  BarChart3,
  BookOpen,
  Server,
  Target,
  Zap,
  Plug,
};

function iconFor(name: string): ComponentType<{ className?: string }> {
  return ICON_MAP[name] ?? Plug;
}

/** Notification Center — matches coremonit-integration IA */
export const NOTIFICATION_CENTER_NAV: IntegrationMenuItem[] = [
  { id: NOTIFICATION_CENTER_BASE, label: 'Overview', icon: Grid3x3, glowColor: 'cyber-purple' },
  {
    id: `${NOTIFICATION_CENTER_BASE}/routing-studio`,
    label: 'Routing Studio',
    icon: Network,
    badge: '⚡',
    glowColor: 'cyber-cyan',
  },
  { id: `${NOTIFICATION_CENTER_BASE}/resource-groups`, label: 'Resource Groups', icon: Server, glowColor: 'cyber-cyan' },
  {
    id: `${NOTIFICATION_CENTER_BASE}/destination-matrix`,
    label: 'Destination Matrix',
    icon: Target,
    glowColor: 'cyber-purple',
  },
  {
    id: `${NOTIFICATION_CENTER_BASE}/communication-hub`,
    label: 'Communication Hub',
    icon: MessageSquare,
    glowColor: 'cyber-cyan',
  },
  { id: `${NOTIFICATION_CENTER_BASE}/ticketing-hub`, label: 'Ticketing Hub', icon: Ticket, glowColor: 'cyber-blue' },
  {
    id: `${NOTIFICATION_CENTER_BASE}/ai-assistant`,
    label: 'Routing AI Assistant',
    icon: Brain,
    badge: 'AI',
    badgeVariant: 'default',
    glowColor: 'cyber-purple',
  },
  {
    id: `${NOTIFICATION_CENTER_BASE}/war-room`,
    label: 'Integration War Room',
    icon: Swords,
    glowColor: 'cyber-magenta',
  },
  { id: `${NOTIFICATION_CENTER_BASE}/testing`, label: 'Testing Center', icon: TestTube2, glowColor: 'cyber-green' },
];

export const NOTIFICATION_CENTER_SECTION: IntegrationMenuItem = {
  id: 'integrations-nc',
  label: 'Notification Center',
  icon: Bell,
  badge: 'NEW',
  badgeVariant: 'default',
  glowColor: 'cyber-purple',
  children: NOTIFICATION_CENTER_NAV,
};

/** Slack suite — full IA from coremonit-integration (routes unchanged) */
export const SLACK_INTEGRATION_SECTION: IntegrationMenuItem = {
  id: 'integrations-slack',
  label: 'Slack',
  icon: MessageSquare,
  badge: '💬',
  badgeVariant: 'secondary',
  glowColor: 'cyber-cyan',
  children: [
    { id: `${SLACK_BASE}/overview`, label: 'Overview', icon: LayoutDashboard, glowColor: 'cyber-cyan' },
    { id: `${SLACK_BASE}/workspaces`, label: 'Workspaces', icon: Users, glowColor: 'cyber-cyan' },
    { id: `${SLACK_BASE}/channels`, label: 'Channels', icon: Hash, glowColor: 'cyber-cyan' },
    { id: `${SLACK_BASE}/routing-studio`, label: 'Alert Routing', icon: Network, glowColor: 'cyber-purple' },
    { id: `${SLACK_BASE}/alert-rules`, label: 'Alert Rules', icon: Target, glowColor: 'cyber-purple' },
    { id: `${SLACK_BASE}/templates`, label: 'Message Templates', icon: FileText, glowColor: 'cyber-cyan' },
    { id: `${SLACK_BASE}/resource-groups`, label: 'Resource Groups', icon: Server, glowColor: 'cyber-cyan' },
    { id: `${SLACK_BASE}/logs`, label: 'Delivery Logs', icon: Activity, glowColor: 'cyber-green' },
    { id: `${SLACK_BASE}/analytics`, label: 'Analytics', icon: BarChart3, glowColor: 'cyber-blue' },
    { id: `${SLACK_BASE}/testing`, label: 'Testing', icon: TestTube2, glowColor: 'cyber-green' },
    { id: `${SLACK_BASE}/help`, label: 'Setup Guide', icon: BookOpen, glowColor: 'cyber-cyan' },
  ],
};

export function buildConnectedIntegrationNav(
  integrations: ConnectedIntegration[],
  /** Provider ids that already have a dedicated static sidebar section */
  excludeIds: string[] = ['slack'],
): IntegrationMenuItem[] {
  return integrations
    .filter((intg) => !excludeIds.includes(intg.id))
    .map((intg) => {
      const template = PROVIDER_NAV_TEMPLATES[intg.id] ?? [];
      const statusBadge =
        intg.status === 'expiring' ? '!' : intg.status === 'error' ? '✕' : undefined;
      return {
        id: `integration-provider-${intg.id}`,
        label: intg.instanceName ? `${intg.name} · ${intg.instanceName}` : intg.name,
        icon: Plug,
        badge: statusBadge,
        badgeVariant: intg.status === 'error' ? 'destructive' : 'secondary',
        glowColor: 'cyber-cyan',
        children: template.map((sub) => ({
          id: sub.path.replace(/^\//, ''),
          label: sub.label,
          icon: iconFor(sub.iconName),
          glowColor: 'cyber-cyan',
        })),
      };
    });
}

/**
 * Top-level Integrations dropdown — mirrors coremonit-integration sidebar IA.
 * Route ids are unchanged (system/integrations, system/notification-center/*, etc.).
 */
export function buildIntegrationsModuleSection(
  integrations: ConnectedIntegration[],
): IntegrationMenuItem {
  return {
    id: 'integrations-module',
    label: 'Integrations',
    icon: Plug,
    badge: 'Hub',
    badgeVariant: 'default',
    glowColor: 'cyber-cyan',
    children: [
      {
        id: INTEGRATIONS_BASE,
        label: 'Integrations Hub',
        icon: Zap,
        badge: 'Hub',
        badgeVariant: 'default',
        glowColor: 'cyber-cyan',
      },
      NOTIFICATION_CENTER_SECTION,
      SLACK_INTEGRATION_SECTION,
      ...buildConnectedIntegrationNav(integrations),
    ],
  };
}
